/**
 * Phase 2L-A101 → A102 — Hidden review duplicate of the homepage.
 *
 * URL: /home-ebook-review
 *
 * A102 originally paired an ebook teaser tile with the
 * "Do we service your area?" lookup in a two-column strip.
 * Aware switched to a national posted-kit service in Feb-2026, so
 * the service-area column was removed from every public surface
 * including this hidden review page. The ebook teaser continues
 * to render as a single centered card that CTAs to /free-guide.
 *
 * Visibility rules (unchanged from A101):
 *   • Hidden from public navigation
 *   • Excluded from sitemap
 *   • Robots: noindex, nofollow, noarchive, nosnippet
 *   • Canonical points back to "/" (real homepage)
 */
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Hero } from "@/components/Hero";
import { TestPackagesStrip } from "@/components/TestPackagesStrip";
import { Differentiator } from "@/components/Differentiator";
import { ReportsShowcase } from "@/components/ReportsShowcase";
import { TestPicker } from "@/components/TestPicker";
import { Pricing } from "@/components/Pricing";
import { TestComparison } from "@/components/TestComparison";
import { HowItWorks } from "@/components/HowItWorks";
import { WaterProblems } from "@/components/WaterProblems";
import { Testimonials } from "@/components/Testimonials";
import { QualityAssurance } from "@/components/QualityAssurance";
import { ServiceAreas } from "@/components/ServiceAreas";
import { FAQ } from "@/components/FAQ";
import { FinalCTA } from "@/components/FinalCTA";
import { SchemaJsonLd } from "@/lib/seo";
import { organizationSchema, websiteSchema, localBusinessSchema, faqSchema } from "@/lib/seo";
import { GLOBAL_FAQS } from "@/lib/faqs";

import EbookTeaser from "@/components/EbookTeaser";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

export default function HomeEbookReview() {
  // Belt-and-braces robots meta — see A101 rationale.
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"][data-a101]');
    if (existing) return;
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow, noarchive, nosnippet";
    m.setAttribute("data-a101", "true");
    document.head.appendChild(m);
  }, []);

  useSEO({
    title: "Homepage Ebook Review (hidden)",
    description:
      "Internal review preview of the Aware ebook lead-magnet section. Not for public traffic.",
    url: `${SEO_DEFAULTS.baseUrl}/`,
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="home-ebook-review-page">
      <SchemaJsonLd data={organizationSchema()} id="jsonld-organization" />
      <SchemaJsonLd data={websiteSchema()} id="jsonld-website" />
      <SchemaJsonLd data={localBusinessSchema()} id="jsonld-localbusiness" />
      <SchemaJsonLd data={faqSchema(GLOBAL_FAQS)} id="jsonld-faq" />
      <ReviewBanner />
      <Header />
      <main>
        <Hero />
        <TestPackagesStrip />

        {/* Phase 2L-A102 (updated Feb-2026): the two-column layout
            paired the EbookTeaser (left) with a "Do we service your
            area?" lookup (right). Aware is now a national posted-kit
            service, so the service-area column was removed. The
            teaser strip continues to render as a single centered
            column that CTAs to /free-guide. */}
        <section
          data-testid="ebook-teaser-row"
          className="bg-slate-50 py-8 lg:py-10 border-y border-slate-100"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
              <EbookTeaser />
            </div>
          </div>
        </section>

        <Differentiator />
        <ReportsShowcase />
        <div id="water-tests" className="scroll-mt-20">
          <TestPicker />
          <Pricing />
          <TestComparison />
        </div>
        <div id="how-it-works" className="scroll-mt-20">
          <HowItWorks />
        </div>
        <WaterProblems />
        <Testimonials />
        <QualityAssurance />
        <ServiceAreas />
        <div id="faq" className="scroll-mt-20">
          <FAQ qas={GLOBAL_FAQS} />
        </div>
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

function ReviewBanner() {
  return (
    <div
      data-testid="home-ebook-review-banner"
      className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-amber-950 text-center text-xs sm:text-sm py-2 px-4 shadow-md font-semibold"
    >
      🔒 HIDDEN REVIEW PAGE — Ebook teaser preview (links to /free-guide).
      Not in nav · not in sitemap · noindex+nofollow.
    </div>
  );
}
